import React, { useState } from 'react';
import './VideoSelectionModal.css';
import { X, Check, FileVideo } from 'lucide-react';

const VideoSelectionModal = ({ isOpen, onClose, submission, onConfirm }) => {
  const [selectedVideos, setSelectedVideos] = useState([]);

  if (!isOpen) return null;

  const handleVideoToggle = (videoId) => {
    setSelectedVideos(prev => 
      prev.includes(videoId) 
        ? prev.filter(id => id !== videoId)
        : [...prev, videoId]
    );
  };

  const handleConfirm = () => {
    const videosToRevise = submission.content.filter(video => 
      selectedVideos.includes(video.id)
    );
    onConfirm(videosToRevise);
    onClose();
  };

  const handleSelectAll = () => {
    if (selectedVideos.length === submission.content.length) {
      setSelectedVideos([]);
    } else {
      setSelectedVideos(submission.content.map(video => video.id));
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="video-selection-modal-overlay" onClick={handleOverlayClick}>
      <div className="video-selection-modal" onClick={(e) => e.stopPropagation()}>
        <div className="video-selection-modal-header">
          <div className="video-selection-modal-title-section">
            <h3 className="video-selection-modal-title">Select Videos for Revision</h3>
            <p className="video-selection-modal-subtitle">
              Choose which videos you'd like to request revisions for
            </p>
          </div>
          <button className="video-selection-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="video-selection-modal-content">
          <div className="video-selection-header">
            <button 
              className="video-selection-select-all-btn"
              onClick={handleSelectAll}
            >
              {selectedVideos.length === submission.content.length ? 'Deselect All' : 'Select All'}
            </button>
            <span className="video-selection-count">
              {selectedVideos.length} of {submission.content.length} selected
            </span>
          </div>

          {/* Main Videos Section */}
          {submission.content.filter(video => {
            const isVariation = video.variationType || video.type === 'hook' || video.type === 'cta';
            return !isVariation;
          }).length > 0 && (
            <div className="video-selection-section">
              <h4 className="video-selection-section-title">Main Videos</h4>
              <div className="video-selection-grid">
                {submission.content.filter(video => {
                  const isVariation = video.variationType || video.type === 'hook' || video.type === 'cta';
                  return !isVariation;
                }).map((video) => (
                  <div 
                    key={video.id}
                    className={`video-selection-item ${selectedVideos.includes(video.id) ? 'selected' : ''}`}
                    onClick={() => handleVideoToggle(video.id)}
                  >
                    <div className="video-selection-checkbox">
                      {selectedVideos.includes(video.id) && <Check size={16} />}
                    </div>
                    
                    <div className="video-selection-thumbnail">
                      {video.thumbnailUrl ? (
                        <img src={video.thumbnailUrl} alt={video.title} />
                      ) : video.downloadURL || video.videoUrl ? (
                        <video 
                          src={video.downloadURL || video.videoUrl} 
                          muted
                          preload="metadata"
                          onLoadedMetadata={(e) => {
                            // Seek to 1 second to get a frame
                            if (e.target) {
                              e.target.currentTime = 1;
                            }
                          }}
                        />
                      ) : (
                        <div className="video-selection-thumbnail-placeholder">
                          <FileVideo size={24} />
                        </div>
                      )}
                    </div>
                    
                    <div className="video-selection-info">
                      <h4 className="video-selection-title">{video.title}</h4>
                      <p className="video-selection-hook">"{video.hook}"</p>
                      <div className="video-selection-type">
                        <span className="video-selection-main">Main Video</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Variations Section */}
          {submission.content.filter(video => {
            const isVariation = video.variationType || video.type === 'hook' || video.type === 'cta';
            return isVariation;
          }).length > 0 && (
            <div className="video-selection-section">
              <h4 className="video-selection-section-title">Variations</h4>
              <div className="video-selection-grid">
                {submission.content.filter(video => {
                  const isVariation = video.variationType || video.type === 'hook' || video.type === 'cta';
                  return isVariation;
                }).map((video) => {
                  // Determine the video type for display
                  const videoType = video.variationType || video.type;
                  const isHook = videoType === 'hook';
                  const isCTA = videoType === 'cta';
                  
                  return (
                    <div 
                      key={video.id}
                      className={`video-selection-item ${selectedVideos.includes(video.id) ? 'selected' : ''}`}
                      onClick={() => handleVideoToggle(video.id)}
                    >
                      <div className="video-selection-checkbox">
                        {selectedVideos.includes(video.id) && <Check size={16} />}
                      </div>
                      
                      <div className="video-selection-thumbnail">
                        {video.thumbnailUrl ? (
                          <img src={video.thumbnailUrl} alt={video.title} />
                        ) : video.downloadURL || video.videoUrl ? (
                          <video 
                            src={video.downloadURL || video.videoUrl} 
                            muted
                            preload="metadata"
                            onLoadedMetadata={(e) => {
                              // Seek to 1 second to get a frame
                              if (e.target) {
                                e.target.currentTime = 1;
                              }
                            }}
                          />
                        ) : (
                          <div className="video-selection-thumbnail-placeholder">
                            <FileVideo size={24} />
                          </div>
                        )}
                      </div>
                      
                      <div className="video-selection-info">
                        <h4 className="video-selection-title">
                          {submission.content.find(v => v.type === video.type && !v.variationType && v.type !== 'hook' && v.type !== 'cta')?.title || video.title}
                          <span className="video-selection-variation-number">
                            {isHook ? 
                              `Hook ${submission.content.filter(v => v && ((v.variationType === 'hook' || v.type === 'hook'))).indexOf(video) + 1}` :
                              isCTA ?
                              `CTA ${submission.content.filter(v => v && ((v.variationType === 'cta' || v.type === 'cta'))).indexOf(video) + 1}` :
                              'Main Video'
                            }
                          </span>
                        </h4>
                        <p className="video-selection-hook">"{video.hook}"</p>
                        <div className="video-selection-type">
                          <span className="video-selection-variation">
                            {isHook ? 'Hook' : isCTA ? 'CTA' : 'Main Video'}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="video-selection-modal-footer">
          <button 
            className="video-selection-cancel-btn"
            onClick={onClose}
          >
            Cancel
          </button>
          <button 
            className="video-selection-confirm-btn"
            onClick={handleConfirm}
            disabled={selectedVideos.length === 0}
          >
            Request Revision for {selectedVideos.length} Video{selectedVideos.length !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VideoSelectionModal;

